import * as styles from "./ReplacementDialog.css";

import { type JSX, type RefObject, useState } from "react";

import { Button } from "@frontend/components/atoms/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@frontend/components/atoms/Dialog";
import {
  Field,
  FieldContent,
  FieldLabel,
} from "@frontend/components/atoms/Field";
import { Icon } from "@frontend/components/atoms/Icon";
import type { IconName } from "@frontend/components/atoms/Icon";
import { Radio, RadioGroup } from "@frontend/components/atoms/Radio";
import {
  TextEditor,
  htmlToPlainText,
  plainTextToHtml,
} from "@frontend/components/organisms/TextEditor";
import { useDiscardGuard } from "@frontend/hooks/useDiscardGuard";
import { useIntlLocale } from "@frontend/hooks/useIntlLocale";
import { DiscardChangesDialog } from "@frontend/shared/discard-changes-dialog";
import { Trans, useLingui } from "@lingui/react/macro";

import type {
  ReplacementDraft,
  ReplacementKind,
  ReplacementSlot,
  ReplacerEmployee,
} from "../types";
import { formatRange } from "../utils/format";
import { ReplacerCombobox } from "./ReplacerCombobox";

const DIALOG_WIDTH = 480;

const DECISIONS: { value: ReplacementKind; icon: IconName }[] = [
  { value: "none", icon: "UserX" },
  { value: "self", icon: "UserCheck" },
  { value: "unassigned", icon: "UserPlus" },
  { value: "employee", icon: "UserRound" },
];

const JUSTIFICATION_MAX = 255;

function subtitle(slot: ReplacementSlot, locale: string): string {
  const name = slot.projectName ?? "";

  return slot.jobBeginDate && slot.jobEndDate
    ? `${name} · ${formatRange(slot.jobBeginDate, slot.jobEndDate, locale)}`
    : name;
}

export type ReplacementDialogProps = {
  slot: ReplacementSlot | null;
  decision: ReplacementDraft;
  from: string | null;
  to: string | null;
  onApply: (decision: ReplacementDraft) => void;
  onClose: () => void;
  originRef?: RefObject<DOMRect | null>;
};

export function ReplacementDialog({
  slot,
  decision,
  from,
  to,
  onApply,
  onClose,
  originRef,
}: ReplacementDialogProps): JSX.Element {
  const { t } = useLingui();
  const locale = useIntlLocale();

  const decisions: Record<
    ReplacementKind,
    { label: string; description: string }
  > = {
    none: {
      label: t`No replacement`,
      description: t`The job is not covered during the absence.`,
    },
    self: {
      label: t`Keeps the job`,
      description: t`The absent employee covers the job anyway.`,
    },
    unassigned: {
      label: t`To be assigned`,
      description: t`A replacement will be chosen later.`,
    },
    employee: {
      label: t`Replaced by`,
      description: t`A colleague covers the job over the period.`,
    },
  };

  const [kind, setKind] = useState<ReplacementKind>(decision.kind);
  const [employee, setEmployee] = useState<ReplacerEmployee | null>(
    decision.employee,
  );
  const [justification, setJustification] = useState(decision.justification);

  const dirty =
    kind !== decision.kind ||
    (employee?.id ?? null) !== (decision.employee?.id ?? null) ||
    justification !== decision.justification;

  const guard = useDiscardGuard({ dirty, onClose });

  const incomplete = "employee" === kind && null === employee;

  const apply = () => {
    if (incomplete) {
      return;
    }

    onApply({
      kind,
      employeeId: "employee" === kind ? (employee?.id ?? null) : null,
      employee: "employee" === kind ? employee : null,
      justification: justification.slice(0, JUSTIFICATION_MAX),
    });
  };

  return (
    <Dialog open onOpenChange={guard.onOpenChange} originRef={originRef}>
      <DialogContent size={DIALOG_WIDTH}>
        <DialogHeader>
          <DialogTitle className={styles.title}>
            {slot ? t`Replacement` : t`Assign to all jobs`}

            <span className={styles.subtitle}>
              {slot ? subtitle(slot, locale) : formatRange(from, to, locale)}
            </span>
          </DialogTitle>

          <DialogClose />
        </DialogHeader>

        <div className={styles.body}>
          <RadioGroup
            aria-label={t`Decision`}
            layout="card"
            indicatorPosition="end"
            value={kind}
            onValueChange={(next) => setKind(next as ReplacementKind)}
          >
            {DECISIONS.map(({ value, icon }) => (
              <Radio
                key={value}
                value={value}
                label={decisions[value].label}
                description={decisions[value].description}
                footer={
                  "employee" === value ? (
                    <ReplacerCombobox
                      value={employee}
                      from={from}
                      to={to}
                      disabled={"employee" !== kind}
                      onValueChange={setEmployee}
                    />
                  ) : undefined
                }
              >
                <Icon name={icon} size="sm" />
              </Radio>
            ))}
          </RadioGroup>

          <Field>
            <FieldLabel>{t`Reason`}</FieldLabel>

            <FieldContent>
              <TextEditor
                size="sm"
                rows={4}
                tools={false}
                ariaLabel={t`Reason`}
                value={plainTextToHtml(justification)}
                onValueChange={(html) =>
                  setJustification(htmlToPlainText(html))
                }
              />
            </FieldContent>
          </Field>
        </div>

        <DialogFooter>
          <Button variant="neutral" onClick={guard.requestClose}>
            <Trans>Cancel</Trans>
          </Button>

          <Button variant="primary" disabled={incomplete} onClick={apply}>
            <Trans>Apply</Trans>
          </Button>
        </DialogFooter>

        <DiscardChangesDialog
          open={guard.confirming}
          onKeep={guard.keep}
          onDiscard={guard.discard}
        />
      </DialogContent>
    </Dialog>
  );
}
